/**
 * PhantomAddPathEdge.tsx - Phantom edge with "Add Path" button
 *
 * This edge renders as a dashed line to the right of the default segment,
 * with an inline "+" button that allows users to add new segments.
 * When clicked, creates a new blank segment and shifts the default segment right.
 */

import { EdgeLabelRenderer, type Position } from '@xyflow/react';
import { css } from 'aphrodite';
import { useState, useCallback } from 'react';
import { PHANTOM_EDGE } from '../utils/layoutConstants';
import type { PhantomAddPathEdgeData } from '../types/edges';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';
import { styleSheet } from './phantomAddPathEdgeStyles';

interface PhantomAddPathEdgeProps {
	id: string;
	sourceX: number;
	sourceY: number;
	targetX: number;
	targetY: number;
	sourcePosition: Position;
	targetPosition: Position;
	selected?: boolean;
	data?: PhantomAddPathEdgeData;
	source: string;
	target: string;
}

export function PhantomAddPathEdge(props: PhantomAddPathEdgeProps) {
	const { id, sourceX, sourceY, targetX, targetY, data } = props;

	const [isAdding, setIsAdding] = useState(false);
	const switchNodeId = data?.switchNodeId;

	// Get the onAddPath callback from context instead of edge data
	const { onAddPath } = useAutomationFlowContext();

	const handleAddPath = useCallback(async () => {
		if (!switchNodeId || !onAddPath || isAdding) {
			return;
		}

		setIsAdding(true);
		try {
			await onAddPath(switchNodeId);
		} finally {
			setIsAdding(false);
		}
	}, [switchNodeId, onAddPath, isAdding]);

	// Calculate midpoint for button placement
	const midY = (sourceY + targetY) / 2;

	// Create dashed path from source to target
	// Use orthogonal routing: down from source, horizontal, up to target area
	const routingY = (sourceY + targetY) / 2;
	const edgePath = `M ${sourceX} ${sourceY} L ${sourceX} ${routingY} L ${targetX} ${routingY} L ${targetX} ${targetY}`;

	return (
		<>
			{/* Dashed phantom edge line */}
			<path
				id={id}
				d={edgePath}
				fill='none'
				stroke={PHANTOM_EDGE.LINE_COLOR}
				strokeWidth={2}
				strokeDasharray={PHANTOM_EDGE.LINE_DASH}
				strokeLinecap='round'
			/>

			{/* Add Path button */}
			<EdgeLabelRenderer>
				<div
					className={css(styleSheet.buttonContainer)}
					style={{
						transform: `translate(-50%, -50%) translate(${targetX}px, ${midY}px)`,
					}}
				>
					<button
						type='button'
						className={css(styleSheet.addButton, isAdding && styleSheet.addButtonLoading)}
						onClick={handleAddPath}
						disabled={isAdding || !switchNodeId}
						aria-label='Add new segment'
						title='Add a new segment to this conditional flow'
					>
						{isAdding ? (
							<svg
								className={css(styleSheet.spinner)}
								width='24'
								height='24'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
							>
								<circle cx='12' cy='12' r='10' strokeWidth='2' strokeLinecap='round' strokeDasharray='60' />
							</svg>
						) : (
							<svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
								<line x1='12' y1='5' x2='12' y2='19' strokeLinecap='round' />
								<line x1='5' y1='12' x2='19' y2='12' strokeLinecap='round' />
							</svg>
						)}
					</button>
				</div>
			</EdgeLabelRenderer>
		</>
	);
}
